import React from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import MirrorText from './MirrorText';

const DASHBOARD_URL = 'https://continualabsdashboardd.netlify.app/login';

/* ── layout ── */
const HeroSection = styled.section`
  position: relative;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: ${p => p.theme.colors.background};
  padding: ${p => p.theme.spacing.xl} ${p => p.theme.spacing.lg};

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: ${p => p.theme.gradients.heroBackground};
    pointer-events: none;
    z-index: 0;
  }
`;

const GridPattern = styled.div`
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(${p => p.theme.colors.borderSubtle} 1px, transparent 1px),
    linear-gradient(90deg, ${p => p.theme.colors.borderSubtle} 1px, transparent 1px);
  background-size: 60px 60px;
  mask-image: radial-gradient(ellipse at center, black 30%, transparent 70%);
  -webkit-mask-image: radial-gradient(ellipse at center, black 30%, transparent 70%);
  pointer-events: none;
  z-index: 0;
  opacity: 0.5;
`;

const GradientOrb = styled(motion.div)`
  position: absolute;
  border-radius: 50%;
  filter: blur(100px);
  opacity: 0.4;
  pointer-events: none;
  z-index: 0;
`;

const Orb1 = styled(GradientOrb)`
  width: 600px; height: 600px;
  background: ${p => p.theme.gradients.glow};
  top: -200px; right: -100px;
`;

const Orb2 = styled(GradientOrb)`
  width: 500px; height: 500px;
  background: ${p => p.theme.gradients.cyanGlow};
  bottom: -150px; left: -100px;
`;

/* ── powered-by badge ── */
const TopBadge = styled(motion.div)`
  position: absolute;
  top: ${p => p.theme.spacing.lg};
  left: ${p => p.theme.spacing.lg};
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  padding: 10px 16px;
  background: ${p => p.theme.colors.surfaceCard};
  backdrop-filter: blur(20px);
  border: 1px solid ${p => p.theme.colors.borderSubtle};
  border-radius: 14px;

  @media (max-width: ${p => p.theme.breakpoints.mobile}) {
    top: ${p => p.theme.spacing.md};
    left: ${p => p.theme.spacing.md};
    padding: 8px 12px;
  }
`;

const PoweredByText = styled.span`
  font-size: 0.5rem;
  color: ${p => p.theme.colors.subtextGray};
  font-weight: ${p => p.theme.typography.weights.medium};
  text-transform: uppercase;
  letter-spacing: 0.1em;
`;

const Logo = styled.img`
  height: 44px; width: auto; object-fit: contain;
  @media (max-width: ${p => p.theme.breakpoints.mobile}) { height: 36px; }
`;

/* ── floating lines ── */
const FloatingElements = styled.div`
  position: absolute;
  top: 50%; left: 0; right: 0;
  height: 400px;
  transform: translateY(-50%);
  pointer-events: none;
  z-index: 1;
  @media (max-width: ${p => p.theme.breakpoints.tablet}) { display: none; }
`;

const FloatingLine = styled(motion.div)`
  position: absolute;
  width: 2px; height: 80px;
  background: linear-gradient(180deg, transparent, ${p => p.theme.colors.accentPurple}, transparent);
  opacity: 0.3;
`;

/* ── content ── */
const Container = styled.div`
  max-width: 780px;
  width: 100%;
  margin: 0 auto;
  position: relative;
  z-index: 2;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Headline = styled(motion.h1)`
  font-size: clamp(1.75rem, 4vw, 3rem);
  font-weight: ${p => p.theme.typography.weights.extrabold};
  color: ${p => p.theme.colors.textLight};
  line-height: 1.15;
  letter-spacing: -0.02em;
  margin-bottom: ${p => p.theme.spacing.sm};

  span {
    background: ${p => p.theme.gradients.primary};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const Subheader = styled(motion.p)`
  font-size: clamp(1rem, 1.5vw, 1.25rem);
  color: ${p => p.theme.colors.subtextGray};
  max-width: 560px;
  line-height: 1.6;
  margin-bottom: ${p => p.theme.spacing.lg};
`;

/* ── benefits ── */
const BenefitsList = styled(motion.ul)`
  list-style: none;
  padding: 0;
  margin: 0 0 ${p => p.theme.spacing.lg};
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px 24px;
  text-align: left;
  max-width: 640px;

  @media (max-width: ${p => p.theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`;

const BenefitItem = styled(motion.li)`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 0.9375rem;
  color: ${p => p.theme.colors.textDark};
  line-height: 1.5;
`;

const CheckCircle = styled.span`
  flex-shrink: 0;
  width: 22px; height: 22px;
  border-radius: 50%;
  background: ${p => p.theme.gradients.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1px;

  svg {
    width: 12px; height: 12px;
    stroke: white;
    stroke-width: 2.5;
    fill: none;
  }
`;

/* ── primary CTA ── */
const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(139, 92, 246, 0.4); }
  50% { box-shadow: 0 0 0 12px rgba(139, 92, 246, 0); }
`;

const PrimaryCTA = styled(motion.a)`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 16px 36px;
  background: ${p => p.theme.gradients.primary};
  color: white;
  border: none;
  border-radius: 14px;
  font-size: 1.0625rem;
  font-weight: ${p => p.theme.typography.weights.semibold};
  text-decoration: none;
  cursor: pointer;
  white-space: nowrap;
  box-shadow: ${p => p.theme.shadows.glow};
  transition: box-shadow 0.3s ease;
  animation: ${pulse} 3s ease-in-out infinite;
  margin-bottom: ${p => p.theme.spacing.sm};

  &:hover {
    box-shadow: 0 8px 30px rgba(139, 92, 246, 0.5);
  }

  @media (max-width: ${p => p.theme.breakpoints.mobile}) {
    padding: 14px 28px;
    font-size: 1rem;
  }
`;

const ArrowIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 8h10M9 4l4 4-4 4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const SocialProof = styled(motion.p)`
  font-size: 0.8125rem;
  color: ${p => p.theme.colors.subtextGray};
  font-style: italic;
  opacity: 0.8;
`;

/* ── component ── */
const benefits = [
  'AI-generated TWI Job Instructions with auto-detected hazards and quizzes',
  'Earn shareable Lean White Belt and Respect-for-People credentials',
  'Strategic planning with Hoshin Kanri X-matrix and monthly reviews',
  'Team collaboration, leaderboards, DISC insights, and real-time audits',
];

const Hero = () => {
  const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.08 } } };
  const fadeUp = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5 } } };

  return (
    <HeroSection>
      <GridPattern />
      <Orb1 animate={{ x: [0, 20, 0], y: [0, -20, 0] }} transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }} />
      <Orb2 animate={{ x: [0, -20, 0], y: [0, 20, 0] }} transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }} />

      <FloatingElements>
        <FloatingLine style={{ top: '10%', right: '15%' }} animate={{ opacity: [0.2, 0.5, 0.2], y: [0, -20, 0] }} transition={{ duration: 4, repeat: Infinity }} />
        <FloatingLine style={{ top: '60%', right: '12%' }} animate={{ opacity: [0.3, 0.6, 0.3], y: [0, -15, 0] }} transition={{ duration: 5, repeat: Infinity, delay: 0.5 }} />
        <FloatingLine style={{ top: '30%', left: '10%' }} animate={{ opacity: [0.2, 0.4, 0.2], y: [0, -25, 0] }} transition={{ duration: 6, repeat: Infinity, delay: 1 }} />
      </FloatingElements>

      <TopBadge initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}>
        <PoweredByText>Powered by</PoweredByText>
        <Logo src="/logo with company name horizontal.png" alt="Continua Labs" />
      </TopBadge>

      <Container>
        <MirrorText primaryWord="NEAL" reflectedWord="LEAN" />

        <Headline initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.9 }}>
          The connected platform for <span>continuous improvement teams</span>
        </Headline>

        <Subheader initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 1 }}>
          Certifications, AI-powered tools, strategic planning, and team workflows — all in one workspace.
        </Subheader>

        <BenefitsList variants={stagger} initial="hidden" animate="visible">
          {benefits.map((b, i) => (
            <BenefitItem key={i} variants={fadeUp}>
              <CheckCircle>
                <svg viewBox="0 0 16 16"><path d="M13 4L6 12L3 9" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </CheckCircle>
              {b}
            </BenefitItem>
          ))}
        </BenefitsList>

        <PrimaryCTA
          href={DASHBOARD_URL}
          target="_blank"
          rel="noopener noreferrer"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.3 }}
          whileHover={{ scale: 1.02, y: -2 }}
          whileTap={{ scale: 0.98 }}
        >
          Get Started — Free <ArrowIcon />
        </PrimaryCTA>

        <SocialProof
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.8 }}
          transition={{ duration: 0.6, delay: 1.5 }}
        >
          Sign up free. Learn, plan, and improve in one connected workspace.
        </SocialProof>
      </Container>
    </HeroSection>
  );
};

export default Hero;
